import ax from "axios";
import RoutesConstant from "../constants/routes";
import { getCookie } from "../utils/cookie-storage/cookie-storage";
import {
  clearAuthCookies,
  getAuthCookies,
  storeAuthCookies,
} from "../utils/cookie-storage/auth-cookie";

// Get base url
const baseUrl = process.env.REACT_APP_ENDPOINT_HTTPS;
let isLoggedOut = false;

const getAxios = ({ baseApiUrl, logoutUrl }) => {
  const axios = ax.create({
    baseURL: `${baseApiUrl}`,
    headers: {
      common: {
        "Content-Type": "application/json",
      },
    },
    timeout: 60000,
  });

  axios.interceptors.request.use(
    (config) => {
      if (isLoggedOut) {
        const source = ax.CancelToken.source();
        config.cancelToken = source.token;
        source.cancel("User logged out. Request blocked.");
      }

      const userToken = getCookie("auth-token");
      if (userToken) {
        config["headers"]["Authorization"] = `Bearer ${userToken}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error?.response);
    }
  );

  let isRefreshing = false;
  let failedQueue = [];

  const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    failedQueue = [];
  };

  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      if (ax.isCancel(error)) return new Promise(() => {});
      const originalRequest = error.config;

      if (
        error?.response?.status === 401 &&
        !originalRequest._retry &&
        getCookie("refresh-token")
      ) {
        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers["Authorization"] = "Bearer " + token;
              return axios(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        isRefreshing = true;

        try {
          const refreshToken = getCookie("refresh-token");
          const res = await ax.post(`${baseUrl}api/v1/users/token`, {
            refreshToken: refreshToken,
          });

          const token = res?.data?.data?.token;

          if (token) {
            storeAuthCookies({
              authToken: token?.accessToken,
              refreshToken: token?.refreshToken,
            });
            processQueue(null, token?.accessToken);
            originalRequest.headers[
              "Authorization"
            ] = `Bearer ${token?.accessToken}`;
            return axios(originalRequest);
          }

          throw new Error("No access token in refresh response");
        } catch (err) {
          processQueue(err, null);
          const { username, deviceId } = getAuthCookies();
          const payload = {
            user_name: username,
            user_type: "User",
          };
          if (deviceId) payload.device_id = deviceId;
          await ax.post(`${baseUrl}api/v1/users/signout`, payload);
          clearAuthCookies();
          window.location.href = logoutUrl;
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error?.response || error);
    }
  );

  return axios;
};

export const axios = getAxios({
  baseApiUrl: baseUrl,
  logoutUrl: RoutesConstant.LOGIN,
});

export const setLoggedOut = (state) => {
  isLoggedOut = state;
};
